// Tracking Utils
import {
  getBiometricPromptPageTypeAndSchema,
  getReloginBiometricsPageTypeAndSchema,
  getReloginPasswordPageTypeAndSchema,
} from '@shopee-user/business-customization-logic';

// Constants & Enums
import {
  BiometricPromptTrackingPoint,
  ReloginBiometricsTrackingPoint,
  ReloginPasswordTrackingPoint,
} from '@shopee-user/tracking-constants';
import { TrackingOperation } from '@shopee-user/tracking';
import { BiometricType } from '../constants';
import { PageSection, TrackingBiometricType } from './types';

// Types
import { TrackingInfo, ImpressionTrackingInfo } from '@shopee-user/tracking';
import {
  TrackingDataFieldBiometricsPrompt,
  TrackingDataBiometricPromptProps,
  TrackingDataFieldReloginBiometrics,
  TrackingDataReloginBiometricsProps,
} from './types';

export function convertBiometricTypeToTrackingData(
  biometricType: BiometricType | string
) {
  switch (biometricType) {
    case BiometricType.FaceId:
      return TrackingBiometricType.IosFace;
    case BiometricType.TouchId:
      return TrackingBiometricType.IosTouch;
    case BiometricType.Fingerprint:
      return TrackingBiometricType.AndroidBiometrics;
    case BiometricType.LockScreenPasscode:
      return TrackingBiometricType.Passcode;
    default:
      return '';
  }
}

// Biometric Prompt Tracker
export function generateTrackingBiometricPromptView({
  biometricType,
}: TrackingDataBiometricPromptProps): TrackingInfo<
  TrackingDataFieldBiometricsPrompt
> {
  return {
    operation: TrackingOperation.View,
    data: {
      biometric_type: convertBiometricTypeToTrackingData(biometricType),
    },
    ...getBiometricPromptPageTypeAndSchema(
      BiometricPromptTrackingPoint.PageView
    ),
  };
}

export function generateTrackingBiometricPromptEnableBiometricsClick({
  biometricType,
}: TrackingDataBiometricPromptProps): TrackingInfo<
  TrackingDataFieldBiometricsPrompt
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'enable_biometric_login',
    data: {
      biometric_type: convertBiometricTypeToTrackingData(biometricType),
    },
    ...getBiometricPromptPageTypeAndSchema(
      BiometricPromptTrackingPoint.EnableBiometricClick
    ),
  };
}

export function generateTrackingBiometricPromptSetupLaterClick({
  biometricType,
}: TrackingDataBiometricPromptProps): TrackingInfo<
  TrackingDataFieldBiometricsPrompt
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'set_up_later',
    data: {
      biometric_type: convertBiometricTypeToTrackingData(biometricType),
    },
    ...getBiometricPromptPageTypeAndSchema(
      BiometricPromptTrackingPoint.SetupLaterClick
    ),
  };
}

export function generateTrackingBiometricPromptBackClick({
  biometricType,
}: TrackingDataBiometricPromptProps): TrackingInfo<
  TrackingDataFieldBiometricsPrompt
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'back_button',
    data: {
      biometric_type: convertBiometricTypeToTrackingData(biometricType),
    },
    ...getBiometricPromptPageTypeAndSchema(
      BiometricPromptTrackingPoint.BackClick
    ),
  };
}

export function generateTrackingBiometricsPromptEnableSuccessImpression({
  biometricType,
}: TrackingDataBiometricPromptProps): ImpressionTrackingInfo<
  TrackingDataFieldBiometricsPrompt
> {
  return {
    operation: TrackingOperation.Impression,
    page_section: PageSection.BiometricsSuccessEnabledPrompt,
    data: {
      biometric_type: convertBiometricTypeToTrackingData(biometricType),
    },
    ...getBiometricPromptPageTypeAndSchema(
      BiometricPromptTrackingPoint.EnableSuccessImpression
    ),
  };
}

export function generateTrackingBiometricsPromptEnableSuccessOkClick({
  biometricType,
}: TrackingDataBiometricPromptProps): TrackingInfo<
  TrackingDataFieldBiometricsPrompt
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'ok_button',
    page_section: PageSection.BiometricsSuccessEnabledPrompt,
    data: {
      biometric_type: convertBiometricTypeToTrackingData(biometricType),
    },
    ...getBiometricPromptPageTypeAndSchema(
      BiometricPromptTrackingPoint.EnableSuccessOkClick
    ),
  };
}

export function generateTrackingBiometricsPromptFailTooManyTimesOkClick({
  biometricType,
}: TrackingDataBiometricPromptProps): TrackingInfo<
  TrackingDataFieldBiometricsPrompt
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'ok_button',
    page_section: PageSection.BiometricsFailedTooManyTimes,
    data: {
      biometric_type: convertBiometricTypeToTrackingData(biometricType),
    },
    ...getBiometricPromptPageTypeAndSchema(
      BiometricPromptTrackingPoint.EnableFailTooManyTimesOkClick
    ),
  };
}

export function generateTrackingBiometricsPromptFailTooManyTimesImpression({
  biometricType,
}: TrackingDataBiometricPromptProps): ImpressionTrackingInfo<
  TrackingDataFieldBiometricsPrompt
> {
  return {
    operation: TrackingOperation.Impression,
    page_section: PageSection.BiometricsFailedTooManyTimes,
    data: {
      biometric_type: convertBiometricTypeToTrackingData(biometricType),
    },
    ...getBiometricPromptPageTypeAndSchema(
      BiometricPromptTrackingPoint.EnableFailTooManyTimesImpression
    ),
  };
}

// Relogin Biometrics Tracker

export function generateTrackingReloginBiometricsPageView({
  biometricType,
}: TrackingDataReloginBiometricsProps): TrackingInfo<
  TrackingDataFieldReloginBiometrics
> {
  return {
    operation: TrackingOperation.View,
    data: {
      biometric_type: convertBiometricTypeToTrackingData(biometricType),
    },
    ...getReloginBiometricsPageTypeAndSchema(
      ReloginBiometricsTrackingPoint.PageView
    ),
  };
}

export function generateTrackingReloginBiometricsBiometricLoginClick({
  biometricType,
}: TrackingDataReloginBiometricsProps): TrackingInfo<
  TrackingDataFieldReloginBiometrics
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'biometric_login',
    data: {
      biometric_type: convertBiometricTypeToTrackingData(biometricType),
    },
    ...getReloginBiometricsPageTypeAndSchema(
      ReloginBiometricsTrackingPoint.ReloginBiometricLoginClick
    ),
  };
}

export function generateTrackingReloginBiometricsLoginWithOtherMethodsClick({
  biometricType,
}: TrackingDataReloginBiometricsProps): TrackingInfo<
  TrackingDataFieldReloginBiometrics
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'login_with_other_methods',
    data: {
      biometric_type: convertBiometricTypeToTrackingData(biometricType),
    },
    ...getReloginBiometricsPageTypeAndSchema(
      ReloginBiometricsTrackingPoint.ReloginWithOtherMethodsClick
    ),
  };
}

export function generateTrackingReloginBiometricsLoginWithAnotherAccountClick({
  biometricType,
}: TrackingDataReloginBiometricsProps): TrackingInfo<
  TrackingDataFieldReloginBiometrics
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'login_with_another_account',
    data: {
      biometric_type: convertBiometricTypeToTrackingData(biometricType),
    },
    ...getReloginBiometricsPageTypeAndSchema(
      ReloginBiometricsTrackingPoint.ReloginWithAnotherAccountClick
    ),
  };
}

export function generateTrackingReloginBiometricsSignupClick({
  biometricType,
}: TrackingDataReloginBiometricsProps): TrackingInfo<
  TrackingDataFieldReloginBiometrics
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'sign_up',
    data: {
      biometric_type: convertBiometricTypeToTrackingData(biometricType),
    },
    ...getReloginBiometricsPageTypeAndSchema(
      ReloginBiometricsTrackingPoint.ReloginSignupClick
    ),
  };
}

export function generateTrackingReloginBiometricsLoginWithOtherMethodsImpression({
  loginSelection,
  biometricType,
  triggeredByError,
}: TrackingDataReloginBiometricsProps & {
  loginSelection: string[];
  triggeredByError: boolean;
}): ImpressionTrackingInfo<
  TrackingDataFieldReloginBiometrics & {
    login_selection: string[];
    triggered_by_error: boolean;
  }
> {
  return {
    operation: TrackingOperation.Impression,
    page_section: PageSection.LoginWithOtherMethodsSelection,
    data: {
      login_selection: loginSelection,
      biometric_type: convertBiometricTypeToTrackingData(biometricType),
      triggered_by_error: triggeredByError,
    },
    ...getReloginBiometricsPageTypeAndSchema(
      ReloginBiometricsTrackingPoint.LoginWithOtherMethodsSelectionImpression
    ),
  };
}

export function generateTrackingReloginBiometricsLoginWithOtherMethodsLoginWithPasswordClick({
  biometricType,
  triggeredByError,
}: TrackingDataReloginBiometricsProps & {
  triggeredByError: boolean;
}): TrackingInfo<
  TrackingDataFieldReloginBiometrics & {
    triggered_by_error: boolean;
  }
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'login_with_password',
    page_section: PageSection.LoginWithOtherMethodsSelection,
    data: {
      biometric_type: convertBiometricTypeToTrackingData(biometricType),
      triggered_by_error: triggeredByError,
    },
    ...getReloginBiometricsPageTypeAndSchema(
      ReloginBiometricsTrackingPoint.LoginWithOtherMethodsLoginWithPasswordClick
    ),
  };
}

export function generateTrackingReloginBiometricsLoginWithOtherMethodsLoginWithPhoneClick({
  biometricType,
  triggeredByError,
}: TrackingDataReloginBiometricsProps & {
  triggeredByError: boolean;
}): TrackingInfo<
  TrackingDataFieldReloginBiometrics & {
    triggered_by_error: boolean;
  }
> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'login_with_phone',
    page_section: PageSection.LoginWithOtherMethodsSelection,
    data: {
      biometric_type: convertBiometricTypeToTrackingData(biometricType),
      triggered_by_error: triggeredByError,
    },
    ...getReloginBiometricsPageTypeAndSchema(
      ReloginBiometricsTrackingPoint.LoginWithOtherMethodsLoginWithPhoneClick
    ),
  };
}

// Relogin Password Tracker
export function generateTrackingReloginPasswordPageView(): TrackingInfo<{}> {
  return {
    operation: TrackingOperation.View,
    ...getReloginPasswordPageTypeAndSchema(
      ReloginPasswordTrackingPoint.PageView
    ),
  };
}

export function generateTrackingReloginPasswordSignupClick(): TrackingInfo<{}> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'sign_up',
    ...getReloginPasswordPageTypeAndSchema(
      ReloginPasswordTrackingPoint.ReloginSignupClick
    ),
  };
}

export function generateTrackingReloginPasswordLoginWithAnotherAccountClick(): TrackingInfo<{}> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'login_with_another_account',
    ...getReloginPasswordPageTypeAndSchema(
      ReloginPasswordTrackingPoint.ReloginWithAnotherAccountClick
    ),
  };
}

export function generateTrackingReloginPasswordLoginWithOtherMethodsClick(): TrackingInfo<{}> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'login_with_other_methods',
    ...getReloginPasswordPageTypeAndSchema(
      ReloginPasswordTrackingPoint.ReloginWithOtherMethodsClick
    ),
  };
}

export function generateReloginPasswordLoginWithPasswordClick(): TrackingInfo<{}> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'login_button',
    ...getReloginPasswordPageTypeAndSchema(
      ReloginPasswordTrackingPoint.ReloginWithPasswordLoginClick
    ),
  };
}

export function generateReloginPasswordForgotPasswordClick(): TrackingInfo<{}> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'forgot_button',
    ...getReloginPasswordPageTypeAndSchema(
      ReloginPasswordTrackingPoint.ForgotPasswordClick
    ),
  };
}

export function generateReloginPasswordLoginWithOtherMethodsImpression({
  loginSelection,
}: {
  loginSelection: string[];
}): ImpressionTrackingInfo<{}> {
  return {
    operation: TrackingOperation.Impression,
    target_type: 'login_with_other_methods_selection',
    ...getReloginPasswordPageTypeAndSchema(
      ReloginPasswordTrackingPoint.LoginWithOtherMethodsSelectionImpression
    ),
    data: {
      login_selection: loginSelection,
    },
  };
}

export function generateReloginPasswordLoginWithOtherMethodsPhoneLoginClick(): TrackingInfo<{}> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'login_with_phone',
    page_section: PageSection.LoginWithOtherMethodsSelection,
    ...getReloginPasswordPageTypeAndSchema(
      ReloginPasswordTrackingPoint.LoginWithOtherMethodsLoginWithPhoneClick
    ),
  };
}

export function generateReloginPasswordLoginWithOtherMethodsBiometricsLoginClick({
  biometricType,
}: {
  biometricType: BiometricType | string;
}): TrackingInfo<{ biometric_method: string }> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'login_with_biometrics',
    data: {
      biometric_method: convertBiometricTypeToTrackingData(biometricType),
    },
    page_section: PageSection.LoginWithOtherMethodsSelection,
    ...getReloginPasswordPageTypeAndSchema(
      ReloginPasswordTrackingPoint.LoginWithOtherMethodsLoginWithBiometricsClick
    ),
  };
}
